import React, { useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import Grid from "@mui/material/Grid2";

import {
  DescriptionCard,
  DraggableCard,
  PenPortraitCard,
  TopBrandsCard,
  TopMindsetCard,
} from "@/components/atoms";
import { DemographicsSection, PenPortraitMap } from "@/components/molecules";
import { useInsightFilters } from "@/context";
import {
  InsightMeta,
  InsightResult,
  LocationResult,
  PenPortrait,
  PenPortraitInsightVariable,
  PenPortraitSection,
} from "@/types";
import { getClientComponent } from "@/utils";

interface PenPortraitViewProps {
  insights: InsightResult[];
  pen_portrait: PenPortrait;
  locations: LocationResult;
  meta?: InsightMeta;
  displayExtra?: boolean;
  editable?: boolean;
  mindsets?: PenPortraitInsightVariable[];
}

export const PenPortraitView: React.FC<PenPortraitViewProps> = ({
  meta,
  insights,
  pen_portrait,
  locations,
  displayExtra = false,
  editable = true,
  mindsets,
}) => {
  const {
    filters: { scoreType, levels },
  } = useInsightFilters();

  const [sections, setSections] = useState<PenPortraitSection[]>([]);

  useEffect(() => {
    // Add mindset section to the start
    if (mindsets) {
      setSections([
        { title: "mindset", id: 99, score: 0 },
        ...pen_portrait.sections,
      ]);
    } else {
      setSections([...pen_portrait.sections]);
    }
  }, [pen_portrait, mindsets]);

  const handleMoveCard = (dragIndex: number, hoverIndex: number) => {
    const updatedSections = [...sections];
    // Swap the card positions
    [updatedSections[hoverIndex], updatedSections[dragIndex]] = [
      updatedSections[dragIndex],
      updatedSections[hoverIndex],
    ];
    setSections(updatedSections);
  };

  const handleRemoveSection = (index: number) => {
    const updatedSections = sections.filter((_, i) => i !== index);
    setSections(updatedSections);
  };

  const ClientDemographicsSection = getClientComponent(
    "DemographicsSection",
    DemographicsSection
  );

  return (
    <Grid container size={12} spacing={2}>
      <Grid container size={12} justifyContent="space-between">
        <Grid container size={8}>
          <ClientDemographicsSection
            insights={insights}
            score={scoreType.value}
          />
          {displayExtra && (
            <Grid size={12}>
              <TopBrandsCard
                variables={pen_portrait.top_scores}
                score={scoreType.value}
                levels={levels}
              />
            </Grid>
          )}
          {meta && (
            <Grid size={12}>
              <DescriptionCard
                description={meta.description_short}
                editable={editable}
              />
            </Grid>
          )}
        </Grid>
        <Grid size={4}>
          <PenPortraitMap scores={locations} extra={displayExtra} />
        </Grid>
      </Grid>

      <DndProvider backend={HTML5Backend}>
        {sections.map((section, index) => {
          if (section.title === 'mindset' && mindsets) {
            return (
              <Grid size={4} key={`pen-portrait-mindsets`}>
                <DraggableCard
                  index={index}
                  onMoveCard={handleMoveCard}
                  type="card"
                >
                  <TopMindsetCard
                    title={'What are their mindsets?'}
                    variables={mindsets}
                    score={scoreType.value}
                    onRemoveSection={() => handleRemoveSection(index)}
                    editable={editable}
                  />
                </DraggableCard>
              </Grid>
            );
          } else {
            const variables = pen_portrait.insight.find(
              (v) => v.section === section.title
            )?.variables;
            if (variables) {
              return (
                <Grid size={4} key={`pen-portrait-${index}`}>
                  <DraggableCard
                    index={index}
                    onMoveCard={handleMoveCard}
                    type="card"
                  >
                    <PenPortraitCard
                      title={section.title}
                      variables={variables}
                      score={scoreType.value}
                      levels={levels}
                      onRemoveSection={() => handleRemoveSection(index)}
                      editable={editable}
                    />
                  </DraggableCard>
                </Grid>
              );
            } else {
              return null
            }
          }
        })}
      </DndProvider>
    </Grid>
  );
};

export default PenPortraitView;
